import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import CustomModal from 'src/components/configuracion/components/customModal';
import EmailDropdown from 'src/components/repositorio/EmailDropdown';
import { useSendBitacora } from 'src/graphql/bitacora';
import { importAlertState } from 'src/redux/actions/alert';
import { useDispatch, useSelector } from 'react-redux';
import { UserContext } from 'src/context';
import { Roles } from 'src/utils/roles';
import { renderBitacoraCommentWithMentions } from './renderMarkdown';
import './BitacoraNotificationModal.scss';

const BitacoraNotificationModal = ({ open, onClose, bitacora, companyId, onConfirm }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { roles } = useContext(UserContext);
  const { selectedCompany } = useSelector((state) => state.companyList);
  const sendBitacora = useSendBitacora();

  const [selectedEmails, setSelectedEmails] = useState([]);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const canNotify = roles?.includes(Roles.ADMIN) || roles?.includes(Roles.SUPER_COLABORADOR);

  useEffect(() => {
    if (open && bitacora) {
      setSelectedEmails([]);
      setIsConfirmed(false);
      setIsLoading(false);
    }
  }, [open, bitacora?.id]);

  const handleConfirm = async () => {
    if (!canNotify || !isConfirmed || selectedEmails.length === 0 || !bitacora?.id) return;

    setIsLoading(true);
    try {
      const cleanEmails = (selectedEmails || [])
        .map((e) => (typeof e === 'string' ? e : e?.email))
        .filter(Boolean);

      const { error: sendError } = await sendBitacora.executeMutation(bitacora.id, cleanEmails);
      if (sendError) throw sendError;

      dispatch(importAlertState({ content: 'La contraparte fue notificada', state: 'success', duration: 5 }));
      onConfirm?.();
      onClose?.();
    } catch (e) {
      const message = e?.message || e?.graphQLErrors?.[0]?.message || 'Error al enviar la bitácora';
      dispatch(importAlertState({ content: message, state: 'error', duration: 5 }));
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedEmails([]);
    setIsConfirmed(false);
    onClose?.();
  };

  const docTitle = (bitacora?.title || '').trim();
  const periodLabel =
    docTitle ||
    (bitacora?.flowDate
      ? moment.utc(bitacora.flowDate).locale('es').format('D [de] MMMM [de] YYYY')
      : 'Bitácora');
  const companyName = bitacora?.companyName || selectedCompany?.name || selectedCompany?.alias || '';
  const latestEmailComment = (bitacora?.comments || [])
    .slice()
    .reverse()
    .find((c) => c?.source === 'email');
  const latestSentClientComment = latestEmailComment?.comment || '';
  const storedClientComment = bitacora?.emailDraft || '';
  const isDelivered = bitacora?.status === 'delivered';
  const clientCommentRaw = isDelivered
    ? (latestSentClientComment || storedClientComment)
    : (storedClientComment || latestSentClientComment);
  const hasClientComment = Boolean(String(clientCommentRaw || '').trim());
  const isFormValid = isConfirmed && selectedEmails.length > 0;

  if (!bitacora) return null;

  return (
    <CustomModal
      open={open}
      onClose={handleCancel}
      title={`Bitácora Flujo de Caja - ${periodLabel}`}
      content={
        <div className="bitacora-notification-modal-content">
          <div className="bitacora-notification-subtitle">{companyName}</div>

          <div className="bitacora-notification-email-field">
            <label className="bitacora-notification-label">Resumen de bitácora a notificar:</label>
            <div className="bitacora-notification-summary-wrap">
              <div className="bitacora-notification-summary-item">
                <span className="bitacora-notification-summary-key">Período</span>
                <span className="bitacora-notification-summary-value">{periodLabel}</span>
              </div>
              <div className="bitacora-notification-summary-block">
                <div className="bitacora-notification-summary-block-title">Comentario inicial (cliente)</div>
                {hasClientComment ? (
                  <div className="bitacora-notification-client-comment bitacora-detail-comments-box">
                    <div
                      className="bitacora-detail-comments bitacora-detail-comments--markdown"
                      dangerouslySetInnerHTML={{ __html: renderBitacoraCommentWithMentions(clientCommentRaw) }}
                    />
                  </div>
                ) : (
                  <div className="bitacora-notification-summary-empty">No hay comentario inicial para cliente.</div>
                )}
              </div>

              <div className="bitacora-notification-summary-note">
                Se enviará la notificación con el acceso directo a esta bitácora en Flujo de Caja.
              </div>
            </div>
          </div>

          <div className="bitacora-notification-field">
            <label className="bitacora-notification-label" htmlFor="bitacora-notification-emails">
              Se hará envió a las siguientes contrapartes:
            </label>
            <EmailDropdown
              id="bitacora-notification-emails"
              value={selectedEmails}
              onChange={setSelectedEmails}
              placeholder="Seleccione emails"
              companyId={companyId || bitacora?.companyId}
              disabled={!canNotify}
            />
            {!canNotify && (
              <div className="bitacora-notification-error-text">
                Su usuario no tiene permisos necesarios para informar productos
              </div>
            )}
            <div className="bitacora-notification-help-text">
              Si falta una contraparte, debes gestionarlo en configuración.{' '}
              <a
                href="/configuracion#contrapartes"
                className="bitacora-notification-help-link"
                onClick={(e) => {
                  e.preventDefault();
                  history.push('/configuracion#contrapartes');
                }}
              >
                Gestionar contrapartes
              </a>
            </div>
          </div>

          <div className="bitacora-notification-checkbox-wrapper">
            <label className="bitacora-notification-checkbox-label">
              <input
                type="checkbox"
                checked={isConfirmed}
                onChange={(e) => setIsConfirmed(e.target.checked)}
                className="bitacora-notification-checkbox"
                disabled={!canNotify}
              />
              <span style={!canNotify ? { opacity: 0.5, cursor: 'not-allowed' } : {}}>
                Confirmo que la bitácora está lista para ser notificada
              </span>
            </label>
          </div>
        </div>
      }
      confirmLabel="Notificar"
      confirmDisabled={!canNotify || !isFormValid || isLoading}
      isLoading={isLoading}
      cancelarLabel="Cancelar"
      onConfirm={handleConfirm}
      size="tiny"
      classNameModal="bitacora-notification-modal"
      classNameContent="bitacora-notification-modal-content-wrapper"
      dimmer={{ style: { zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)' } }}
      modalStyle={{
        maxWidth: '90%',
        minWidth: '720px',
        maxHeight: '90vh',
        overflow: 'hidden',
        margin: '0 auto',
      }}
    />
  );
};

export default BitacoraNotificationModal;
